import axios from 'axios';
import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaPause, FaStepForward, FaStepBackward, FaVolumeUp, FaVolumeMute, FaChevronUp, FaChevronDown, FaTimes, FaPlus, FaShare, FaBroadcastTower, FaListUl, FaTrash } from 'react-icons/fa';
import AddToPlaylistModal from './AddToPlaylistModal';
import ShareModal from './ShareModal';
import SleepTimer from './SleepTimer';
import '../styles/SleepTimer.css';
import '../styles/Player.css';
import Equalizer from './Equalizer';
import LyricsDisplay from './LyricsDisplay';

function Player({ currentTrack, playlist, isPlaying, setIsPlaying, onNext, onPrevious, onTrackSelect, onClose, queue = [], onRemoveFromQueue, onClearQueue, addToQueue }) {
  const audioRef = useRef(null);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const castPlayerRef = useRef(null);
  const [playlistTrack, setPlaylistTrack] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [showEQ, setShowEQ] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);
  const historyLogged = useRef(false);
  const [crossfade, setCrossfade] = useState(0);
  const [showCrossfade, setShowCrossfade] = useState(false);
  const crossfadeRef = useRef(null);
  const crossfadeTimerRef = useRef(null);
  useEffect(() => {
    if (!currentTrack || !audioRef.current) return;
    historyLogged.current = false;
    const audio = audioRef.current;
    const logHistory = () => {
      if (historyLogged.current) return;
      const listened = Math.floor(audio.currentTime);
      if (listened < 10) return;
      historyLogged.current = true;
      const completed = audio.duration > 0 && (audio.duration - audio.currentTime) < 3;
      axios.post("/api/history/log", { track_id: currentTrack.id, album_id: currentTrack.album_id, duration_listened: listened, completed }).catch(() => {});
    };
    audio.addEventListener("pause", logHistory);
    audio.addEventListener("ended", logHistory);
    return () => { audio.removeEventListener("pause", logHistory); audio.removeEventListener("ended", logHistory); };
  }, [currentTrack]);

  const isRadio = currentTrack?.isRadio === true;

  const getAudioUrl = (fp) => {
    if (!fp) return '';
    if (fp.startsWith('http')) return fp;
    return `https://disney-music.gpworld.fr${fp.startsWith('/') ? fp : '/'+fp}`;
  };

  // Cast Framework
  useEffect(() => {
    const initCast = () => {
      if (!window.cast || !window.cast.framework) return;
      try {
        const ctx = window.cast.framework.CastContext.getInstance();
        ctx.setOptions({ receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID, autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED });
        const player = new window.cast.framework.RemotePlayer();
        const controller = new window.cast.framework.RemotePlayerController(player);
        castPlayerRef.current = { player, controller };
        controller.addEventListener(window.cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED, (e) => setIsCasting(e.value));
        controller.addEventListener(window.cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED, () => { if (isCasting && player.currentTime) setCurrentTime(player.currentTime); });
        controller.addEventListener(window.cast.framework.RemotePlayerEventType.DURATION_CHANGED, () => { if (isCasting && player.duration) setDuration(player.duration); });
      } catch (e) { console.log('Cast init error:', e); }
    };
    if (window.cast && window.cast.framework) initCast();
    else window['__onGCastApiAvailable'] = (ok) => { if (ok) initCast(); };
  }, [isCasting]);

  const loadTrackOnCast = (track) => {
    if (!isCasting || !window.cast?.framework) return;
    const session = window.cast.framework.CastContext.getInstance().getCurrentSession();
    if (!session) return;
    const mediaInfo = new window.chrome.cast.media.MediaInfo(getAudioUrl(track.file_path), 'audio/mpeg');
    mediaInfo.metadata = new window.chrome.cast.media.MusicTrackMediaMetadata();
    mediaInfo.metadata.title = track.title; mediaInfo.metadata.artist = track.artist;
    const request = new window.chrome.cast.media.LoadRequest(mediaInfo); request.autoplay = isPlaying;
    session.loadMedia(request).catch(err => console.error('Cast load error:', err));
  };

  // Media Session
  useEffect(() => {
    if (!currentTrack || !('mediaSession' in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({ title: currentTrack.title, artist: currentTrack.artist, artwork: currentTrack.cover_url ? [{ src: (currentTrack.cover_url.startsWith('http') ? currentTrack.cover_url : 'https://disney-music.gpworld.fr' + currentTrack.cover_url), sizes: '512x512', type: 'image/png' }] : [{ src: '/artwork/artwork-96.png', sizes: '96x96', type: 'image/png' }, { src: '/artwork/artwork-192.png', sizes: '192x192', type: 'image/png' }, { src: '/artwork/artwork-512.png', sizes: '512x512', type: 'image/png' }] });
    navigator.mediaSession.setActionHandler('play', () => setIsPlaying(true));
    navigator.mediaSession.setActionHandler('pause', () => setIsPlaying(false));
    if (!isRadio) {
      navigator.mediaSession.setActionHandler('previoustrack', onPrevious);
      navigator.mediaSession.setActionHandler('nexttrack', onNext);
    }
  }, [currentTrack, onNext, onPrevious, setIsPlaying, isRadio]);

  // Changement de piste
  useEffect(() => {
    if (!currentTrack) return;
    if (isCasting) { loadTrackOnCast(currentTrack); return; }
    if (audioRef.current) {
      const url = getAudioUrl(currentTrack.file_path);
      console.log('🎵 Lecture:', url, '| Radio:', isRadio);
      audioRef.current.src = url;
      crossfadeRef.current = false;
      if (crossfadeTimerRef.current) clearInterval(crossfadeTimerRef.current);
      audioRef.current.volume = volume;
      audioRef.current.load();
      if (isPlaying) audioRef.current.play().catch(err => console.error('Erreur lecture:', err));
    }
  }, [currentTrack, isCasting]);

  // Play/Pause
  useEffect(() => {
    if (isCasting && castPlayerRef.current) {
      const { player, controller } = castPlayerRef.current;
      if (isPlaying && player.isPaused) controller.playOrPause();
      else if (!isPlaying && !player.isPaused) controller.playOrPause();
    } else if (audioRef.current) {
      if (isPlaying) audioRef.current.play().catch(err => console.error('Erreur lecture:', err));
      else audioRef.current.pause();
    }
  }, [isPlaying, isCasting]);

  // Time updates
  useEffect(() => {
    if (isCasting) return;
    const audio = audioRef.current; if (!audio) return;
    const updateTime = () => { if (!isRadio) setCurrentTime(audio.currentTime); };
    const updateDuration = () => { if (!isRadio) setDuration(audio.duration); };
    const handleEnded = () => { if (!isRadio) onNext(); };
    const handleError = () => { 
      const e = audio.error; 
      console.error('❌ Audio error:', { code: e?.code, message: e?.message, src: audio.src }); 
      if (isRadio && isPlaying) {
        setTimeout(() => {
          console.log('📻 Reconnexion radio...');
          audio.src = getAudioUrl(currentTrack.file_path);
          audio.load();
          audio.play().catch(console.error);
        }, 3000);
      }
    };
    audio.addEventListener('timeupdate', updateTime); audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded); audio.addEventListener('error', handleError);
    return () => { audio.removeEventListener('timeupdate', updateTime); audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded); audio.removeEventListener('error', handleError); };
  }, [onNext, isCasting, isRadio, isPlaying, currentTrack]);

  const formatTime = (s) => { if (isNaN(s)) return '0:00'; return `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`; };
  
  const handleSeek = (e) => { 
    if (isRadio) return;
    const t = (e.target.value/100)*duration; 
    if (isCasting && castPlayerRef.current) { castPlayerRef.current.player.currentTime = t; castPlayerRef.current.controller.seek(); } 
    else if (audioRef.current) audioRef.current.currentTime = t; 
    setCurrentTime(t); 
  };
  
  const handleVolumeChange = (e) => { 
    const v = parseFloat(e.target.value); setVolume(v); setIsMuted(v===0); 
    if (isCasting && castPlayerRef.current) { castPlayerRef.current.player.volumeLevel = v; castPlayerRef.current.controller.setVolumeLevel(); } 
    else if (audioRef.current) audioRef.current.volume = v; 
  };
  
  const toggleMute = () => { if (isMuted) handleVolumeChange({target:{value:volume||0.5}}); else handleVolumeChange({target:{value:0}}); };
  const progress = !isRadio && duration > 0 ? (currentTime/duration)*100 : 0;

  return (
    <>
      <div className={`player-compact ${isExpanded ? 'expanded' : ''} ${isRadio ? 'radio-mode' : ''}`}>
        <audio ref={audioRef} />
        <div className="player-bar">
          <button className="player-close-btn" onClick={onClose} title="Fermer"><FaTimes /></button>
          
          <div className="player-info">
            <div className="player-title">
              {isRadio && <FaBroadcastTower className="radio-icon-player" />}
              {currentTrack?.title}
            </div>
            <div className="player-artist">
              {currentTrack?.artist}
              {isRadio && <span className="radio-live-badge">EN DIRECT</span>}
              {isCasting && <span className="casting-badge">📡 Cast</span>}
            </div>
          </div>

          <div className="player-controls-compact">
            {!isRadio && <button onClick={onPrevious} className="ctrl-btn" title="Précédent"><FaStepBackward /></button>}
            <button onClick={() => setIsPlaying(!isPlaying)} className="ctrl-btn play-btn-compact" title={isPlaying ? 'Pause' : 'Lecture'}>
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
            {!isRadio && <button onClick={onNext} className="ctrl-btn" title="Suivant"><FaStepForward /></button>}
          </div>

          {isRadio ? (
            <div className="player-radio-info">
              <span className="radio-wave">📻</span>
              <span className="radio-stream-text">Radio Disney Village - Stream live</span>
            </div>
          ) : (
            <div className="player-progress-compact">
              <span className="time-compact">{formatTime(currentTime)}</span>
              <input type="range" min="0" max="100" value={progress} onChange={handleSeek} className="progress-slider-compact" />
              <span className="time-compact">{formatTime(duration)}</span>
            </div>
          )}

          {!isRadio && (
            <div className="player-extra-actions">
              {currentTrack && (<>
                <button className="ctrl-btn" onClick={() => setPlaylistTrack(currentTrack)} title="Ajouter à une playlist"><FaPlus /></button>
                <button className="ctrl-btn" onClick={() => setShareItem({ type: 'track', trackId: currentTrack.id, title: currentTrack.title, artist: currentTrack.artist })} title="Partager"><FaShare /></button>
              </>)}
              <button
                className={`ctrl-btn queue-toggle-btn ${queue.length > 0 ? 'has-queue' : ''}`}
                onClick={() => { setShowQueue(!showQueue); if (!showQueue) setIsExpanded(false); }}
                title={`File d'attente (${queue.length})`}
              >
                <FaListUl />
                {queue.length > 0 && <span className="queue-badge">{queue.length}</span>}
              </button>
            </div>
          )}

          <div className="player-volume-compact">
            <google-cast-launcher className="cast-button"></google-cast-launcher>
            <button onClick={toggleMute} className="ctrl-btn" title={isMuted ? 'Réactiver' : 'Muet'}>{isMuted ? <FaVolumeMute /> : <FaVolumeUp />}</button>
            <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={handleVolumeChange} className="volume-slider-compact" />
          </div>
          <SleepTimer onTimerEnd={() => setIsPlaying(false)} />
          <button className="player-control-btn" onClick={() => { setShowEQ(!showEQ); setShowLyrics(false); }} title="Égaliseur">🎛️</button>
          <button className="player-control-btn" onClick={() => { setShowLyrics(!showLyrics); setShowEQ(false); }} title="Paroles">🎤</button>
          <button className="player-control-btn" onClick={() => setShowCrossfade(!showCrossfade)} title="Crossfade">🔀</button>
          <button className="player-expand-btn" onClick={() => { setIsExpanded(!isExpanded); if (!isExpanded) setShowQueue(false); }} title={isExpanded ? "Réduire" : "Playlist"}>
            {isExpanded ? <FaChevronDown /> : <FaChevronUp />}
          </button>
        </div>

        {/* File d'attente */}
        {showQueue && !isRadio && (
          <div className="player-queue-expanded">
            <div className="queue-header">
              <h3>File d'attente ({queue.length})</h3>
              {queue.length > 0 && (
                <button className="queue-clear-btn" onClick={onClearQueue}>
                  <FaTrash /> Vider
                </button>
              )}
            </div>
            {queue.length === 0 ? (
              <div className="queue-empty">
                <p>La file d'attente est vide</p>
                <p className="queue-hint">Utilisez le bouton "Lire ensuite" sur une piste pour l'ajouter</p>
              </div>
            ) : (
              <div className="playlist-scroll">
                {queue.map((track, i) => (
                  <div key={`q-${track.id}-${i}`} className="playlist-item-compact queue-item">
                    <div className="playlist-item-main" onClick={() => { onTrackSelect(track); setIsPlaying(true); }}>
                      <span className="track-num-compact queue-num">{i + 1}</span>
                      <div className="track-info-compact">
                        <div className="track-name-compact">{track.title}</div>
                        <div className="track-artist-compact">{track.artist || track.album_artist}</div>
                      </div>
                      <span className="track-dur-compact">{formatTime(track.duration)}</span>
                    </div>
                    <button className="playlist-action-btn queue-remove-btn" onClick={() => onRemoveFromQueue(track.id)} title="Retirer">
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Playlist */}
        {isExpanded && !isRadio && !showQueue && (
          <div className="player-playlist-expanded">
            <h3>Playlist ({playlist.length} pistes)</h3>
            <div className="playlist-scroll">
              {playlist.map((track, i) => {
                const isCurrent = track.id === currentTrack?.id;
                const prev = playlist[i-1];
                const showDisc = !prev || prev.disc_number !== track.disc_number;
                return (
                  <React.Fragment key={track.id}>
                    {showDisc && playlist.some(t => t.disc_number > 1) && <div className="disc-header-compact">CD {track.disc_number}</div>}
                    <div className={`playlist-item-compact ${isCurrent ? 'active' : ''}`}>
                      <div className="playlist-item-main" onClick={() => { onTrackSelect(track); setIsPlaying(true); }}>
                        <span className="track-num-compact">{track.track_number}</span>
                        <div className="track-info-compact"><div className="track-name-compact">{track.title}</div><div className="track-artist-compact">{track.artist}</div></div>
                        <span className="track-dur-compact">{formatTime(track.duration)}</span>
                      </div>
                      <div className="playlist-item-actions">
                        {addToQueue && (
                          <button className="playlist-action-btn" onClick={(e) => { e.stopPropagation(); addToQueue(track); }} title="Lire ensuite">
                            <FaListUl />
                          </button>
                        )}
                        <button className="playlist-action-btn" onClick={(e) => { e.stopPropagation(); setPlaylistTrack(track); }} title="Ajouter à une playlist"><FaPlus /></button>
                        <button className="playlist-action-btn" onClick={(e) => { e.stopPropagation(); setShareItem({ type: 'track', trackId: track.id, title: track.title, artist: track.artist }); }} title="Partager"><FaShare /></button>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
        {showEQ && <Equalizer onClose={() => setShowEQ(false)} />}
        {showLyrics && currentTrack && <LyricsDisplay trackId={currentTrack.id} currentTime={currentTime} onClose={() => setShowLyrics(false)} />}
        {showCrossfade && (
          <div className="crossfade-panel">
            <div className="crossfade-header">
              <span>🔀 Crossfade</span>
              <button onClick={() => setShowCrossfade(false)}>✕</button>
            </div>
            <div className="crossfade-options">
              {[0, 2, 4, 6, 8, 10].map(s => (
                <button key={s} className={crossfade === s ? "active" : ""} onClick={() => setCrossfade(s)}>
                  {s === 0 ? "Off" : s + "s"}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {playlistTrack && <AddToPlaylistModal track={playlistTrack} onClose={() => setPlaylistTrack(null)} />}
      {shareItem && <ShareModal item={shareItem} onClose={() => setShareItem(null)} />}
    </>
  );
}

export default Player;
